import './datasourceRiskScoreWidget.less'
import { RiskScoreTooltipText } from '../../../../configureRiskScore/components/riskScoreTooltipText/riskScoreTooltipText'
import { RiskDensityTooltipText } from '../../../../riskScore/components/riskDensityTooltip'
import ChartBarSimple from '../../../../../components/ChartBarSimple/chartBarSimple'
import { RootState } from '../../../../../rootReducer'
import {
  ACTION_FETCH_RISK_SCORE_BY_DATASOURCE_ID,
  fetchRiskScoreByDataSourceId,
  resetDataSourceRiskScoreData
} from '../../../../configureRiskScore/riskScoreSlice'
import useUrlContext from '../../../../../hooks/useUrlContext'
import { getShowLoader } from '../../../../../reducers/requestReducer'
import RiskScoreCustomTooltip from '../../components/riskScoreCustomTooltip'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_RISK_SCORE,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_DOCUMENTS_ALL,
  URL_DOCUMENTS_CLASSIFIED
} from '../../../../../constants'
import { managePageFiltersMultiple } from '../../../../../utils/urlUtil'
import { mockDsRiksScoreWidgetData } from '../../../renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../../constants'
import { getDataSourceBasePath } from '../../../../../utils/dataSourceUtil'
import { Info } from 'phosphor-react'
import { connect } from 'react-redux'
import { Col, Tooltip, Typography } from '@lightbeamai/design-system'
import React, { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  riskScoreGraphData?: any
  riskDensity?: number
  showLoader: boolean
}

interface DispatchProps {
  fetchRiskScoreByDataSourceId: (arg: {
    dataSourceId: string
    [UNIQUE_WIDGET_INDEX]: string
  }) => void
  resetDataSourceRiskScoreData: () => void
}

type DataSourceRiskScoreWidgetComponentProps = StateProps & DispatchProps & OwnProps

const DataSourceRiskScoreWidget = ({
  riskDensity = 0,
  riskScoreGraphData = [],
  showLoader,
  fetchRiskScoreByDataSourceId,
  useMockData = false,
  widgetDatasourceId,
  widgetDashboardId,
  widgetDatasourceType,
  resetDataSourceRiskScoreData
}: DataSourceRiskScoreWidgetComponentProps) => {
  const intl = useIntl()
  const context = useUrlContext()
  const dataSourceId = widgetDatasourceId
    ? widgetDatasourceId
    : (context.contextFilters.datasourceId as string)

  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)

  useEffect(() => {
    if (useMockData) return
    fetchRiskScoreByDataSourceId({ dataSourceId, [UNIQUE_WIDGET_INDEX]: widgetIndex })
  }, [dataSourceId, fetchRiskScoreByDataSourceId, useMockData, widgetIndex])

  useEffect(() => {
    return () => {
      resetDataSourceRiskScoreData()
    }
  }, [resetDataSourceRiskScoreData])

  const riskDensityValue = useMemo(
    () => (riskDensity < 10 ? 'low' : riskDensity < 40 ? 'average' : 'high'),
    [riskDensity]
  )

  const pathArr = getDataSourceBasePath(
    widgetDatasourceType,
    dataSourceId,
    context.pathname,
    true
  ) as string[]

  const redirectUrl = pathArr.join('/') + URL_DOCUMENTS_ALL

  const formattedRiskScoreGraphData = useMemo(() => {
    const riskScoreGraphDataResult = riskScoreGraphData?.map((item) => ({
      ...item,
      onClick: () =>
        managePageFiltersMultiple({
          pathname: redirectUrl,
          pageName: URL_DOCUMENTS_CLASSIFIED,
          filters: [
            {
              filterKey: PAGE_VIEW_KEY,
              filterValues: [PAGE_VIEW_VALUES.list],
              action: 'add'
            },
            {
              filterKey: FILTER_RISK_SCORE,
              filterValues: [item.name],
              action: 'add'
            }
          ],
          newQueryParams: {
            ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
            ...(dataSourceId && { [DATA_SOURCE_ID]: dataSourceId })
          }
        })
    }))
    return riskScoreGraphDataResult
  }, [riskScoreGraphData])

  return (
    <div className="lb-widget-card xs-p-16">
      <div className="datasource-risk-score-widget">
        <Typography className="risk-score-widget-heading font-600 xs-pb-16" as="h4">
          <FormattedMessage id="dataSources.widget.riskScore.title" />
        </Typography>
        <div className="xs-p-16 risk-score-distribution-widget-row">
          <Col span={5} className="xs-pr-16 risk-density-col">
            <div className="xs-mb-16">
              <Typography variant={'baseMinus2'} className="font-500 xs-mr-8" as="span">
                <FormattedMessage id="dataSources.widget.riskScore.section1.title" />
              </Typography>
              <Tooltip title={<RiskDensityTooltipText />} placement="top">
                <Info size={12} weight="bold" />
              </Tooltip>
            </div>
            <div className="risk-score-density-graph">
              <Typography variant={'baseMinus2'} className="risk-density-value font-500">
                {intl.formatMessage({
                  id: 'dataSources.widget.riskScoreDensity.graph.value'
                })}
                &nbsp;
                <span>{intl.formatMessage({ id: `risk.${riskDensityValue}` })}</span>
              </Typography>
              <div className="risk-density-bar">
                {!showLoader && (
                  <>
                    <span
                      className="risk-density-bar-indicator"
                      style={{ left: `calc(${riskDensity}% - ${riskDensity < 5 ? 0 : 3}px)` }}
                    ></span>
                    <span
                      className="risk-density-bar-value"
                      style={{
                        left: `${riskDensity - (riskDensity > 90 ? 10 : 0)}%`
                      }}
                    >
                      {riskDensity}%
                    </span>
                  </>
                )}
              </div>
            </div>
          </Col>
          <Col span={7} className="xs-pl-16">
            <div>
              <Typography variant={'baseMinus2'} className="font-500 xs-mr-8" as="span">
                <FormattedMessage id="dataSources.widget.riskScore.section2.title" />
              </Typography>
              <Tooltip title={<RiskScoreTooltipText />} placement="top">
                <Info size={12} weight="bold" />
              </Tooltip>
            </div>
            <ChartBarSimple
              data={formattedRiskScoreGraphData}
              barColor="var(--color-scanned-data-documents-bar)"
              className="xs-mt-16"
              xAxisAngle={0}
              customToolTip={
                <RiskScoreCustomTooltip
                  title={intl.formatMessage({
                    id: 'objects'
                  })}
                />
              }
              yAxisLabelPadding={{ dx: -10 }}
              xAxisLabelPadding={{ dy: 15 }}
              barSize={36}
              chartResponsiveHeight={200}
              cartesianGridHorrizontal={true}
              xLabel={intl.formatMessage({ id: 'dataSources.widget.riskScore.graph.xAxis' })}
              yLabel={intl.formatMessage({ id: 'dataSources.widget.riskScore.graph.yAxis' })}
            />
          </Col>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
  return {
    riskDensity: ownProps?.useMockData
      ? mockDsRiksScoreWidgetData.riskDensity
      : state.riskScores.dataSourceRiskScoreData?.[widgetIndex]?.riskDensity,
    riskScoreGraphData: ownProps?.useMockData
      ? mockDsRiksScoreWidgetData.riskScoreGraphData
      : state.riskScores.dataSourceRiskScoreData?.[widgetIndex]?.riskScoreGraphData,
    showLoader: getShowLoader(ACTION_FETCH_RISK_SCORE_BY_DATASOURCE_ID)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchRiskScoreByDataSourceId,
  resetDataSourceRiskScoreData
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(DataSourceRiskScoreWidget)
